/**
 * What to do about a message somebody sent.
 *
 * This is the whole of the game's side of the wire: twenty-nine kinds of
 * message, the checks each one has to pass, and what the world is told to do
 * about it. It used to be the middle of `server/main.ts`, which meant the
 * only machine that could ever run a world was ours.
 *
 * It knows three things and no more: a `Host` (which worlds there are, who
 * has been knocking, where to write down what happened), a `Guest` (somebody
 * with a name, a position and a `Link` to speak down), and the message. It
 * does not know what the link is made of, where the worlds are kept, or what
 * is listening. Put a `Host` round it in a browser and the same rules run on
 * a player's machine.
 *
 * What is *not* here: the listening itself, the message budget, and the
 * heartbeat - those belong to whatever is holding the connections, and on
 * our machine that is still `server/main.ts`.
 */

import type { Host } from "./host.js";
import type { Link } from "./link.js";
import { pieces, snapshotObject, snapshotText, type Field } from "./snapshot.js";
import { Realm, REACH, type RealmClient } from "./realm.js";
import {
  PROTOCOL_VERSION, plausibleMove, wordMatches,
  type ClientMessage, type PlayerState, type DroneState,
} from "./protocol.js";
import {
  Budget, RAISE_QUEUE_MAX, PATIENCE, MAX_PLAYERS,
} from "./limits.js";
import { nameProblem, passwordProblem, emailLooksReal } from "./accounts.js";
import { PREP_SECONDS } from "../world/war.js";
import { isReplaceable, BlockId } from "../block/registry.js";
import { ORC_REACH } from "../entity/orc.js";
import { buildingNamed } from "../world/blueprint.js";
import { mapById } from "../world/maps.js";
import type { ItemStack } from "../inventory/inventory.js";

/**
 * The most one blow may claim.
 *
 * The damage travels because it is whatever is in the striker's hand and the
 * server does not keep their bag. A ceiling costs nothing and stops the
 * obvious: one message that fells the ватажок.
 */
const MAX_STRIKE = 12;

/**
 * The most slots a bag may claim to have.
 *
 * A rucksack is thirty-six and what is worn is four; these are ceilings
 * rather than the truth, because the truth is the client's and a stranger
 * must not be able to make this process hold a megabyte of empty slots.
 */
const MAX_BAG_SLOTS = 64;
const MAX_WORN_SLOTS = 8;

export interface Guest extends RealmClient, Link {
  /**
   * When they got in, so the journal can say how long they stayed.
   *
   * Nought until they are actually in a world: somebody who opened a socket
   * to read the list of worlds and closed it again has not played anything,
   * and counting that as a session of nought minutes would put a stranger's
   * glance in the same column as an evening.
   */
  since: number;
  /**
   * The world they are standing in, or null while they are still in the door.
   *
   * Every message that touches blocks, orcs or people goes through this, so
   * there is exactly one place that decides whose world a client is in - and
   * one that has to be null-checked, which is what `ready` already meant.
   */
  realm: Realm | null;
  /** When their last accepted position arrived, for the speed check. */
  lastMoveAt: number;
  /** The address to count their knocking against. */
  readonly from: string;
  /** Ordinary messages. */
  readonly budget: Budget;
  /**
   * How far over budget they have got: a drop adds one, a pass takes one off.
   *
   * A running score rather than a run, because a run resets - and a steady
   * flood just over the ceiling slips one message through often enough to
   * reset it for ever, which is exactly what it did.
   */
  strikes: number;
  /**
   * Buildings dropped for being too far ahead of the queue, in a row.
   *
   * Counted apart from `strikes` because an ordinary message resets those,
   * and a client sending raises fast enough to overrun the queue is sending
   * them slowly enough to stay inside the message budget - so the two floods
   * would cancel each other out and neither would ever be noticed.
   */
  overQueue: number;
}

/*
 * Shorthand, and the one place the router says "tell them".
 *
 * A closed link drops it silently - that check lives in the link now, with
 * the thing that knows what closed means.
 */
function send(client: Guest, message: object): void {
  client.send(message);
}

export function route(
  host: Host, client: Guest, message: ClientMessage,
): void {
  /*
   * Names, where anybody keeps them.
   *
   * Ours does; a world running in somebody's browser does not, and every
   * case below that needs one answers the way it answers a name that was
   * never registered. None of them says which it was - telling a stranger
   * "there are no accounts here" and "no such name" apart is the whole
   * thing those answers are careful not to do.
   */
  const names = host.names;
  switch (message.t) {
    /*
     * Taking a name and holding it.
     *
     * Answered and done with: the page then joins the way it always does.
     * The word is not asked for here - registering is not entering a world,
     * and somebody who has not been told the word has still chosen a name
     * nobody else may have.
     */
    case "register": {
      const name = String(message.name ?? "").trim();
      const problem = nameProblem(name) ??
        passwordProblem(String(message.password ?? ""));
      if (problem !== null) {
        send(client, { t: "account", ok: false, why: problem });
        return;
      }
      if (names === null) {
        send(client, { t: "account", ok: false, why: "name-taken" });
        return;
      }
      const email = String(message.email ?? "").trim();
      if (email.length > 0 && !emailLooksReal(email)) {
        send(client, { t: "account", ok: false, why: "email-odd" });
        return;
      }
      /*
       * A name somebody is already playing under is not free.
       *
       * Otherwise registering would be a way to take a name off a guest who
       * has been using it all week.
       */
      const claimed = [...host.realms.values()].some((r) => r.nameClaimed(name));
      const made = claimed
        ? { ok: false as const }
        : names.register(name, String(message.password), email);
      if (!made.ok) {
        send(client, { t: "account", ok: false, why: "name-taken" });
        console.log(`register refused for "${name}"`);
        return;
      }
      names.save();
      send(client, { t: "account", ok: true, token: made.token });
      console.log(`registered "${name}"${email.length > 0 ? " with an address" : ""}`);
      return;
    }

    /*
     * "Make me a world."
     *
     * An account and its token, checked the same way joining checks them:
     * making a world is a directory and a heap on somebody else's machine,
     * and a stranger who opened a socket has not earned one. Answered with
     * the world's name, and the page then joins it the way it joins any
     * other - so making and entering stay two acts, and the second is the
     * one every check already covers.
     */
    case "create": {
      const name = String(message.name ?? "").trim();
      // A token if they have one, a password if they have only that - the
      // same two ways the door takes, and for the same reason.
      const token = String(message.token ?? "");
      // Nowhere to check a password is nowhere to make a world from.
      if (names === null) {
        send(client, { t: "made", ok: false, why: "sign-in" });
        return;
      }
      const signedIn = names.hasToken(name, token) ||
        names.login(name, String(message.password ?? "")).ok;
      if (!signedIn) {
        send(client, { t: "made", ok: false, why: "sign-in" });
        return;
      }
      const word = String(message.word ?? "").trim();
      const built = host.make(String(message.map ?? ""), name, word, {
        bots: message.bots === true,
        // Minutes on the screen, seconds on the wire, and a floor of one:
        // a preparing of nought would put two armies in each other's
        // trenches with the wall coming down as they arrived.
        prep: Math.max(60, Math.min(60 * 60,
          Math.round(Number(message.prep ?? PREP_SECONDS)))),
      });
      if (!built.ok) {
        send(client, { t: "made", ok: false, why: built.why });
        return;
      }
      send(client, { t: "made", ok: true, world: built.realm.id });
      return;
    }

    /*
     * "I have forgotten it."
     *
     * The same answer whatever happens - no such name, no address on it,
     * Postmark having a bad afternoon. Any difference between those tells
     * somebody working through names which of them exist, which is most of
     * what they wanted.
     */
    case "login": {
      const name = String(message.name ?? "").trim();
      const signed = names === null
        ? { ok: false as const }
        : names.login(name, String(message.password ?? ""));
      if (!signed.ok || signed.token === undefined) {
        // The one answer for a wrong name and a wrong password alike.
        send(client, { t: "account", ok: false, why: "wrong" });
        return;
      }
      send(client, { t: "account", ok: true, token: signed.token });
      return;
    }
    case "forgot": {
      const name = String(message.name ?? "").trim();
      if (names === null) {
        send(client, { t: "account", ok: true, why: "reset-sent" });
        return;
      }
      const begun = names.beginReset(name);
      if (begun !== null) {
        names.save();
        host.post?.(begun.to, name, begun.ticket);
      }
      send(client, { t: "account", ok: true, why: "reset-sent" });
      return;
    }

    /* A ticket out of that message, and a new password. */
    case "reset": {
      const problem = passwordProblem(String(message.password ?? ""));
      if (problem !== null) {
        send(client, { t: "account", ok: false, why: problem });
        return;
      }
      if (names === null) {
        send(client, { t: "account", ok: false, why: "reset-stale" });
        return;
      }
      const done = names.finishReset(
        String(message.ticket ?? ""), String(message.password));
      if (done) names.save();
      send(client, done
        ? { t: "account", ok: true, why: "reset-done" }
        : { t: "account", ok: false, why: "reset-stale" });
      return;
    }

    case "hello": {
      /*
       * Once only.
       *
       * A second hello on a socket that is already in a world would join a
       * second one without leaving the first, and the first would go on
       * sending snapshots to somebody who is no longer there. Knocking to see
       * the list and then saying which world is two sockets, not two hellos.
       */
      if (client.ready) return;
      if (message.v !== PROTOCOL_VERSION) {
        send(client, {
          t: "no",
          code: "version",
          why: `this server speaks protocol ${PROTOCOL_VERSION}, you speak ${message.v}`,
        });
        client.close();
        return;
      }
      /*
       * Which world - and if they did not say, the list of them.
       *
       * **No word for the list.** The word belongs to the world now, so
       * there is nothing to check before one is named. What that costs is
       * that somebody who finds the address learns which worlds are here and
       * how many people are in them; what it buys is that the word you hand
       * out opens the world you meant and not every other world standing on
       * the same machine. The count is not "who is playing" - no name, no
       * seed, no size, and nothing at all about what is in it.
       *
       * It must not grow anything either: a stranger who opened a socket has
       * not asked for a кремль to be built.
       */
      if (message.world === undefined) {
        send(client, {
          t: "worlds",
          list: [
            ...[...host.realms.values()].map((r) => ({
              id: r.id, map: r.map.id, name: r.map.name, blurb: r.map.blurb,
              players: r.players, awake: r.isGrown,
              // The padlock, not the word: whether one will be asked for.
              locked: r.word.length > 0,
              ...(r.owner !== null ? { owner: r.owner } : {}),
            })),
            /*
             * Then the worlds on players' machines.
             *
             * After ours, not mixed in: the list is read top to bottom, and
             * the worlds this machine answers for are the ones somebody
             * should see first. Each carries `guest`, which is what tells
             * the page that joining means being introduced to another
             * computer rather than walking in here - and that nothing which
             * happens in there counts for anything.
             *
             * A guest world is always awake. There is nothing to grow: it
             * is already standing on the machine that made it.
             */
            ...(host.board?.all() ?? []).map((w) => {
              const map = mapById(w.map);
              return {
                id: w.id, map: w.map, name: map.name, blurb: map.blurb,
                players: w.players, awake: true, locked: w.locked,
                owner: w.owner, guest: true as const,
              };
            }),
          ],
        });
        return;
      }

      const realm = host.realms.get(String(message.world));
      if (realm === undefined) {
        send(client, {
          t: "no", code: "world",
          why: `no world called ${String(message.world)} here`,
        });
        client.close();
        return;
      }

      /*
       * That world's word, before anything else is done for them.
       *
       * Checked before the world is grown or joined, so a stranger gets
       * nothing at all: not the seed, not the size, not who is playing.
       * Being turned away should tell you only that you were.
       */
      if (!wordMatches(realm.word, String(message.word ?? ""))) {
        /*
         * A wrong word is counted, not just refused.
         *
         * Refusing alone is refusing an unlimited number of times, which for
         * a word two people can say out loud means the word is decorative.
         * Five, then the address goes quiet for ten minutes.
         *
         * Counted per address rather than per world: somebody working
         * through the words of one world is somebody working through the
         * words, and letting them start again on the next door would make
         * the allowance as wide as the number of worlds.
         */
        const lastStraw = host.doorman.wrongWord(client.from, Date.now());
        send(client, { t: "no", code: "word", why: "wrong word" });
        console.log(`#${client.id} gave the wrong word for ${realm.id}` +
          (lastStraw ? ` - ${client.from} is barred for ten minutes` : ""));
        client.close();
        return;
      }
      // They are who they said. A near miss on the way in should not count
      // against them next time.
      host.doorman.rightWord(client.from, Date.now());

      /*
       * Trying the door rather than coming through it.
       *
       * The home screen asks before it reloads to join, so that a mistyped
       * word is a sentence on the screen rather than a кремль grown for
       * somebody who is not coming in.
       */
      if (message.look === true) {
        send(client, { t: "open", world: realm.id });
        return;
      }

      /*
       * A world that is full says so, rather than letting everybody in and
       * being slow for all of them. Counted per world, not per server: two
       * maps sharing a process is not two maps sharing a crowd.
       */
      if (realm.players >= MAX_PLAYERS) {
        send(client, { t: "no", code: "full", why: `this world holds ${MAX_PLAYERS}` });
        client.close();
        return;
      }

      /*
       * And a race that has already started is closed.
       *
       * The gathering minute is the door. After it, letting somebody in
       * either restarts the race for everybody already running it or drops
       * them into one they cannot win - and both of those were reported from
       * the same game.
       */
      if (!realm.openToJoiners) {
        send(client, {
          t: "no", code: "late",
          why: "цей забіг уже почався — зачекай наступного",
        });
        client.close();
        return;
      }

      /*
       * And their name, which has to be theirs.
       *
       * After the word and after the `look`, in that order and on purpose: a
       * wrong word must not be able to claim a name, and knocking to see
       * whether a word fits must not take one either.
       *
       * Trimmed before it is judged, because " Гриць" and "Гриць" are the
       * same person typing and would otherwise be two people.
       */
      const wanted = String(message.name ?? "").trim().slice(0, 24);

      /*
       * An account, when the name has one.
       *
       * Checked before the world's own claim and instead of it: a registered
       * name is the machine's to give out, not a world's, and the tag a
       * browser keeps for a guest name has no authority over it. Without
       * that order, whoever claimed the name in a world first would outrank
       * the person whose password it is.
       *
       * Logging in stays optional. A name nobody has registered falls
       * through to the claim it always used.
       */
      if (names !== null && names.has(wanted)) {
        const known = names.hasToken(wanted,
          typeof message.token === "string" ? message.token : undefined);
        const allowed = known
          ? { ok: true as const, token: String(message.token) }
          : names.login(wanted,
            typeof message.password === "string" ? message.password : "");
        if (!allowed.ok) {
          send(client, { t: "no", code: "name", why: `${wanted} has a password` });
          console.log(`#${client.id} could not log in as "${wanted}"`);
          client.close();
          return;
        }
        /*
         * And still not twice at once.
         *
         * One name is one body, account or guest: two would be two backs
         * answering to one crown and reaching into one bag.
         */
        for (const other of realm.clients.values()) {
          if (!other.ready || other.name !== wanted) continue;
          send(client, {
            t: "no", code: "name", why: `${wanted} is already in this world`,
          });
          client.close();
          return;
        }
        client.name = wanted;
        client.skinId = String(message.skinId).slice(0, 32);
        client.realm = realm;
        realm.join(client);
        const at = realm.spawnPoint();
        client.x = at.x; client.y = at.y; client.z = at.z;
        client.ready = true;
        send(client, {
          t: "welcome", v: PROTOCOL_VERSION, you: client.id,
          worldId: realm.id, token: allowed.token,
          world: realm.worldSeed(), tick: Date.now(),
          time: realm.cycle.t,
        });
        const theirs = realm.bagFor(client.name);
        if (theirs !== undefined) send(client, { t: "bag", bag: theirs });
        realm.sayWar();
        console.log(
          `#${client.id} ${client.name} logged in to ${realm.id} from ${client.from}`);
        client.since = Date.now();
        host.note("join", client.name, realm.id, realm.map.id);
        return;
      }

      const claim = realm.claimName(wanted,
        typeof message.token === "string" ? message.token : undefined);
      if (!claim.ok) {
        send(client, { t: "no", code: "name", why: claim.why });
        console.log(`#${client.id} could not have "${wanted}": ${claim.why}`);
        client.close();
        return;
      }
      client.name = wanted;
      client.skinId = String(message.skinId).slice(0, 32);
      client.realm = realm;
      realm.join(client);
      // Growing it, if this is the first person to ask for this map.
      const at = realm.spawnPoint();
      client.x = at.x; client.y = at.y; client.z = at.z;
      client.ready = true;
      send(client, {
        t: "welcome",
        v: PROTOCOL_VERSION,
        you: client.id,
        worldId: realm.id,
        token: claim.token,
        world: realm.worldSeed(),
        tick: Date.now(),
        time: realm.cycle.t,
      });
      /*
       * And whatever this world was keeping for them.
       *
       * Only when there is something: somebody arriving in a world for the
       * first time keeps what they walked in with, rather than being handed
       * an empty bag and losing it.
       */
      const kept = realm.bagFor(client.name);
      if (kept !== undefined) send(client, { t: "bag", bag: kept });
      /*
       * And, on «Сучасна війна», where the match has got to.
       *
       * Everybody, not only whoever just arrived: the lobby is a list of
       * teams and who is on them, and the person who has this moment walked
       * in is a new line in everybody else's copy of it.
       */
      realm.sayWar();
      // The address as well as the name: it is what limits are counted
      // against, so an operator who cannot see it cannot tell whether the
      // proxy in front is passing it through or handing over its own.
      console.log(`#${client.id} ${client.name} is in ${realm.id} from ${client.from}`);
      client.since = Date.now();
      host.note("join", client.name, realm.id, realm.map.id);
      return;
    }

    case "ready": {
      const realm = client.realm;
      if (realm === null) return;
      const world = realm.world;
      /*
       * The world they grew has to be the world we have.
       *
       * They were sent a seed and grew their own terrain from it, which is
       * what makes handing out a world nearly free. If what came back is not
       * what we have, they are in a different world - and two people in
       * different worlds is worse than one person who cannot get in, because
       * it looks like it is working.
       */
      if (message.seed !== world.opts.seed ||
          message.sizeXZ !== world.opts.sizeXZ ||
          message.sizeY !== world.opts.sizeY) {
        send(client, {
          t: "no",
          code: "world",
          why: `you grew ${message.seed}/${message.sizeXZ}x${message.sizeY}, ` +
            `this world is ${world.opts.seed}/${world.opts.sizeXZ}x${world.opts.sizeY}`,
        });
        console.log(`#${client.id} grew the wrong world - refused`);
        client.close();
        return;
      }
      return;
    }

    case "move": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const now = Date.now();
      const dt = (now - client.lastMoveAt) / 1000;
      /*
       * Believed only if they could have got there.
       *
       * Not a full replay of their movement - that is a bigger machine than
       * this game needs - but enough to refuse the person who says they are
       * four hundred blocks away inside a locked room. Refusing sends them
       * back rather than dropping them: a lag spike is not cheating, and
       * kicking somebody for a bad connection would be worse than the cheat.
       */
      if (!plausibleMove(client, message, dt)) {
        send(client, { t: "where", x: client.x, y: client.y, z: client.z });
        client.lastMoveAt = now;
        return;
      }
      /*
       * And nobody walks while everybody is still arriving.
       *
       * The page stops taking orders during the minute before a round on the
       * hill, which is enough for anybody playing the game - and not enough
       * for a page that has been edited, which is the only kind of player a
       * head start would be worth anything to. Sent back where they were
       * rather than dropped: the same answer a lag spike gets.
       *
       * Their heading is not refused. Standing still and looking round while
       * you wait is the whole of what the minute is for.
       */
      if (realm.frozen) {
        client.yaw = message.yaw; client.pitch = message.pitch;
        client.lastMoveAt = now;
        send(client, { t: "where", x: client.x, y: client.y, z: client.z });
        return;
      }
      client.x = message.x; client.y = message.y; client.z = message.z;
      client.yaw = message.yaw; client.pitch = message.pitch;
      // Creative, which the server cannot see for itself: what it decides is
      // whether the orcs come after them. Nothing else reads it, so an old
      // client that never sends it is simply somebody who is walking.
      client.flying = message.fly === true;
      client.lastMoveAt = now;
      /*
       * And their drone, checked only for being in the world.
       *
       * Not for speed: where somebody's drone is tells nobody anything they
       * could not already see, so a client that lies about it is drawing a
       * picture for other people and gaining nothing. Being inside the world
       * is checked because a number off the map would put a marker where
       * there is nothing to stand on and nobody could work out why.
       */
      client.drone = plausibleDrone(realm, message.drone)
        ? message.drone ?? null : null;
      /*
       * And what is in their hand.
       *
       * Bounded rather than checked: an id has to be a number small enough to
       * be an id, because the thing at the other end looks a picture up by it
       * and a stranger must not be able to send anything else. Which id it is
       * is theirs to say - bags are not authoritative yet, and a drawn axe
       * that is not really in the bag is a picture, not an advantage.
       */
      client.held = Number.isFinite(message.held) &&
        (message.held ?? 0) >= 0 && (message.held ?? 0) < 1024
        ? Math.floor(message.held ?? 0) : 0;
      client.blows = Number.isFinite(message.blows)
        ? Math.floor(message.blows ?? 0) : 0;
      return;
    }

    /*
     * A block changed to something, within arm's length.
     *
     * The same trust as `place` and `break`: the id is the client's to choose
     * and the reach is the server's to check. Without the `isReplaceable`
     * test, because the things that come this way - a door, a stove, a чан -
     * are changes to a cell that is occupied by definition.
     */
    case "set": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const { x, y, z, id } = message;
      const world = realm.world;
      if (!world.withinBounds(x, y, z)) return;
      if (Math.hypot(x + 0.5 - client.x, y + 0.5 - client.y, z + 0.5 - client.z)
        > REACH + 2) return;
      if (world.getBlock(x, y, z) === id) return;
      world.setBlock(x, y, z, id);
      world.updateWater(x, y, z);
      world.updateLava(x, y, z);
      world.updateOil(x, y, z);
      realm.touch();
      realm.pending.push({ x, y, z, id });
      return;
    }

    case "place":
    case "break": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const { x, y, z } = message;
      const world = realm.world;
      if (!world.withinBounds(x, y, z)) return;
      // Within arm's length of where they are standing. The client checks
      // this too; the client is not who this is for.
      if (Math.hypot(x + 0.5 - client.x, y + 0.5 - client.y, z + 0.5 - client.z)
        > REACH + 2) return;

      if (message.t === "break") {
        /*
         * The шопопало-блок is opened by the world, not by the page.
         *
         * The realm keeps the count and does the rolling, so everybody at
         * that block walks down one table; a creature it shakes out is put
         * into the world here and reaches everybody in the next snapshot,
         * and anything you can carry is sent back to the one who hit it.
         */
        if (world.getBlock(x, y, z) === BlockId.Random) {
          const gift = realm.openOneBlock();
          if (gift !== null) {
            send(client, { t: "gave", item: gift.item, count: gift.count });
          }
          return;
        }
        // Through the realm: on «Король гори» a block may take its neighbours
        // and whoever is standing on them with it.
        // What they are holding, because two blocks in the game refuse to
        // come out for the wrong thing: the незламний блок for anything at
        // all, and a прапор for anything but адамантій.
        realm.breakBlock(x, y, z, client.held);
        return;
      }
      if (!isReplaceable(world.getBlock(x, y, z))) return;
      world.setBlock(x, y, z, message.id);
      // And the water beside it, which a wall may have just stopped or a
      // bucket just started. It arrives on the clock, like everything else.
      world.updateWater(x, y, z);
      world.updateLava(x, y, z);
      world.updateOil(x, y, z);
      realm.touch();
      realm.pending.push({ x, y, z, id: message.id });
      return;
    }

    /*
     * A blow at an orc.
     *
     * The damage is the client's to name - it is whatever is in their hand,
     * and a bag is not authoritative yet, so this is the same trust `place`
     * already has. The reach is the server's, and that is the part that
     * stops somebody clearing the far island from their doorstep.
     */
    /*
     * The lobby of «Сучасна війна», and the button that ends it.
     *
     * Making a team is the owner's - "він нажимає створити команду" -
     * joining one is anybody's, and both only mean anything before the
     * match starts. Everything is answered with the whole state rather than
     * with "yes": a page that has just joined a team wants to draw the
     * teams, not a tick.
     */
    /*
     * The танк: place one, get in, steer, get out, fire.
     *
     * Every one of them refused off «Сучасна війна» by `deploy` itself, and
     * every claim about a place checked for reach - the same rule the човен
     * has, and the one kind of lie a server can always catch.
     */
    case "deploy": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      if (Math.hypot(message.x - client.x, message.z - client.z) > REACH + 4) {
        return;
      }
      realm.deploy({ x: message.x, y: message.y, z: message.z }, message.yaw);
      return;
    }

    case "mount": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      realm.mount(client.id, message.id, client, REACH + 2);
      return;
    }

    case "steer": {
      if (!client.ready) return;
      client.realm?.steer(client.id, message.yaw);
      return;
    }

    case "dismount": {
      if (!client.ready) return;
      client.realm?.dismount(client.id);
      return;
    }

    case "shell": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      // From somewhere near them: a gun that can be fired from the other
      // side of the map is not a tank, it is a cursor.
      if (Math.hypot(message.x - client.x, message.y - client.y,
        message.z - client.z) > REACH + 4) return;
      realm.fireShell(
        { x: message.x, y: message.y, z: message.z },
        { x: message.vx, y: message.vy, z: message.vz },
        message.from,
      );
      return;
    }

    case "team": {
      const realm = client.realm;
      const war = realm?.war ?? null;
      if (realm === null || war === null) return;
      if (typeof message.make === "string") {
        if (!realm.hosts(client.name)) return;
        war.make(message.make);
      } else if (typeof message.join === "number") {
        war.join(message.join, client.name);
      }
      realm.sayWar();
      return;
    }

    case "begin": {
      const realm = client.realm;
      const war = realm?.war ?? null;
      if (realm === null || war === null) return;
      if (!realm.hosts(client.name)) return;
      const teams = war.begin(realm.world);
      if (teams.length === 0) return;
      /*
       * "Всі гравці з'являються одночасно на мапі", each in their own
       * quarter - and that is where they wake up from now on, which is what
       * `home` says. Without it the second life of every match was a walk
       * back from whatever the world's own spawn happened to be.
       */
      for (const other of realm.everybody()) {
        const team = war.teamOf(other.name);
        if (team < 0) continue;
        const at = war.spawn(team, realm.world);
        other.x = at.x;
        other.y = at.y;
        other.z = at.z;
        other.send({ t: "where", x: at.x, y: at.y, z: at.z, home: true });
      }
      realm.sayWar();
      console.log(`world  ${realm.id} - match begun, ${teams.length} teams`);
      host.note("begun", client.name, realm.id, realm.map.id,
        { teams: teams.length });
      return;
    }

    /*
     * "I have just died."
     *
     * A player's сила is their own page's, so this is the only way the
     * server learns that anybody has died at all - and a match's table of
     * kills and deaths is the one thing that needs to know.
     */
    case "died": {
      const realm = client.realm;
      const war = realm?.war ?? null;
      if (realm === null || war === null || !war.running) return;
      const killer = typeof message.by === "number"
        ? realm.clientById(message.by)?.name ?? null : null;
      war.died(client.name, killer);
      realm.sayWar();
      return;
    }

    case "strike": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const damage = Math.max(0, Math.min(MAX_STRIKE, message.damage));
      /*
       * The same reach for every kind of body.
       *
       * An orc's own reach is added because an orc is the one that comes to
       * you - the two boxes meet before the centres do. A pig is smaller and
       * a карась smaller still, and being a shade generous about a fish is
       * not a thing anybody will farm.
       */
      realm.strike(message.kind, message.id, damage,
        { x: client.x, y: client.y, z: client.z }, ORC_REACH + REACH,
        client.id);
      return;
    }

    /*
     * An arrow, as where it left from and which way it went.
     *
     * The same shape as a bolt and for the same reason: an impact forty
     * blocks off is a claim nothing here could test, a launch is at the
     * shooter's own hand. What it hits is worked out below, in the tick,
     * which is what lets a bow kill an orc that only the server owns.
     */
    case "shoot": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const { x, y, z, dx, dy, dz } = message;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
      if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(dz)) return;
      if (!realm.world.withinBounds(Math.floor(x), Math.floor(y), Math.floor(z))) return;
      if (Math.hypot(x - client.x, y - client.y, z - client.z) > REACH + 3) return;
      if (!realm.shoot({ x, y, z }, { x: dx, y: dy, z: dz })) return;

      // Everybody but the shooter, who has already watched it leave.
      realm.broadcast({ t: "arrow", x, y, z, dx, dy, dz }, client.id);
      return;
    }

    /*
     * Човни: put one down, get in, point it, get out, pick it up.
     *
     * The server owns whether a boat *exists* and who is in it; where it is
     * comes from the rider's own `move`, because while somebody is in a boat
     * the boat is where they are. Everything here is therefore a claim about
     * a place within arm's reach, which is the one kind of claim a server
     * can always check.
     */
    case "launch": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const { x, z } = message;
      if (!Number.isInteger(x) || !Number.isInteger(z)) return;
      if (!realm.world.withinBounds(x, 1, z)) return;
      if (Math.hypot(x + 0.5 - client.x, z + 0.5 - client.z) > REACH + 2) return;
      const boat = realm.launch(x, z, client.yaw);
      if (boat === null) return;
      realm.touch();
      return;
    }

    case "board": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      if (!Number.isInteger(message.id)) return;
      realm.board(client.id, message.id,
        { x: client.x, y: client.y, z: client.z }, REACH + 2);
      return;
    }

    case "row": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const boat = realm.boatOf(client.id);
      if (boat === undefined) return;
      if (!Number.isFinite(message.yaw)) return;
      boat.yaw = message.yaw;
      return;
    }

    case "ashore": {
      if (!client.ready) return;
      client.realm?.ashore(client.id);
      return;
    }

    case "scuttle": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      if (!Number.isInteger(message.id)) return;
      if (realm.scuttle(client.id, message.id,
        { x: client.x, y: client.y, z: client.z }, REACH + 2)) {
        realm.touch();
      }
      return;
    }

    /*
     * A кресало, struck into a cell somebody can reach.
     *
     * Where, not what: the realm looks at the block itself and refuses
     * anything that will not catch, so the only thing worth checking here is
     * that the striker was near enough to reach it. The fire that follows is
     * the server's, which is what makes what burns away burn away for
     * everybody.
     */
    case "spark": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const { x, y, z } = message;
      if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) return;
      if (!realm.world.withinBounds(x, y, z)) return;
      if (Math.hypot(x + 0.5 - client.x, y + 0.5 - client.y, z + 0.5 - client.z)
        > REACH + 2) return;
      if (!realm.spark(x, y, z)) return;
      realm.broadcast({ t: "spark", x, y, z }, client.id);
      return;
    }

    case "bolt": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const { x, y, z, dx, dy, dz } = message;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
      if (!realm.world.withinBounds(Math.floor(x), Math.floor(y), Math.floor(z))) return;
      /*
       * Thrown from somewhere they could have thrown it from.
       *
       * Their own hand, or their drone - which is up there because they sent
       * it up and the server has been told where it is. Anywhere else and the
       * bolt never existed.
       */
      const fromHand = Math.hypot(x - client.x, y - client.y, z - client.z)
        <= REACH + 3;
      const d = client.drone;
      const fromDrone = d !== null &&
        Math.hypot(x - d.x, y - d.y, z - d.z) <= 3;
      if (!fromHand && !fromDrone) return;
      if (!realm.throwBolt({ x, y, z }, { x: dx, y: dy, z: dz })) return;

      // Everybody but the thrower, who has already watched it leave.
      realm.broadcast({ t: "bolt", x, y, z, dx, dy, dz }, client.id);
      return;
    }

    /*
     * Their word for what they are carrying.
     *
     * Bounded rather than believed: a bag is a list of slots and each slot
     * is an id and a count, and a stranger must not be able to make the
     * server hold a megabyte of them. Which items are in it is theirs to say
     * for now - see the `bag` message and NETWORK.md.
     */
    case "bag": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      const clean = (list: unknown, most: number): (ItemStack | null)[] => {
        if (!Array.isArray(list)) return [];
        return list.slice(0, most).map((slot) => {
          if (typeof slot !== "object" || slot === null) return null;
          const { id, count } = slot as { id?: unknown; count?: unknown };
          if (typeof id !== "number" || typeof count !== "number") return null;
          if (!Number.isFinite(id) || id < 0 || id >= 1024) return null;
          if (!Number.isFinite(count) || count <= 0) return null;
          return { id: Math.floor(id), count: Math.min(9999, Math.floor(count)) };
        });
      };
      realm.keepBag(client.name, {
        slots: clean(message.bag?.slots, MAX_BAG_SLOTS),
        worn: clean(message.bag?.worn, MAX_WORN_SLOTS),
      });
      return;
    }

    case "raise": {
      if (!client.ready) return;
      const realm = client.realm;
      if (realm === null) return;
      if (buildingNamed(message.building) === undefined) return;
      /*
       * Queued, not done here, and not refused.
       *
       * A кремль is sixty-two thousand blocks and a quarter of a second of
       * this process doing nothing else - measured, not guessed. Doing them
       * as they arrive means one player can stop the world for everybody.
       *
       * Refusing is not the alternative: the client has already raised it in
       * its own world and sent the instruction after, so a refusal leaves one
       * person standing in a building nobody else can see. The queue drains a
       * building a tick and loses none of them.
       */
      if (!realm.queueRaise(client, message, RAISE_QUEUE_MAX)) {
        // Six deep in a queue that empties fifteen times a second is not
        // somebody building. This one is lost - which does mean that client's
        // world and ours part company, and is the one place that is allowed
        // to happen, because getting here at all takes a script.
        if (++client.overQueue >= PATIENCE) {
          console.log(`#${client.id} is building faster than the world can - closed`);
          client.close();
        }
        return;
      }
      client.overQueue = 0;
      return;
    }

    /*
     * Below here: introducing two players, and then getting out of the way.
     *
     * None of these touches a world. They are how somebody hosting a world
     * in their own browser is found, and how the two machines are handed
     * each other's addresses; after that the game goes straight between
     * them and this server never sees it. The rules about who may say what
     * to whom are in `signal.ts` - this is the part that speaks.
     */

    /** "I am holding a world; put it on the list." */
    case "host": {
      const board = host.board;
      if (board === null || names === null) {
        send(client, { t: "hosting", ok: false, why: "not-here" });
        return;
      }
      const who = String(message.name ?? "").trim();
      /*
       * An account, the same as making a world here.
       *
       * Not because hosting costs us anything - it costs a row in a Map -
       * but because the row wears a name in front of everybody choosing a
       * world, and a name in a list has to mean somebody.
       */
      const signedIn = names.hasToken(who, String(message.token ?? "")) ||
        names.login(who, String(message.password ?? "")).ok;
      if (!signedIn) {
        send(client, { t: "hosting", ok: false, why: "sign-in" });
        return;
      }
      if (message.dry === true) {
        send(client, { t: "hosting", ok: true });
        return;
      }
      const map = mapById(String(message.map ?? ""));
      const opened = board.open(
        client.id, map.id, map.name, map.blurb, who,
        message.locked === true, Date.now());
      if (!opened.ok) {
        send(client, { t: "hosting", ok: false, why: opened.why });
        return;
      }
      send(client, { t: "hosting", ok: true, world: opened.it.id });
      console.log(`guest world ${opened.it.id} on ${who}'s machine (${map.id})`);
      /*
       * Written down as a join: it is the one thing about a world held in a
       * browser that passes through here, and without it the journal showed
       * nothing while people played every evening. No leave and no length -
       * we cannot see inside their world, and a number we made up would be
       * worse than none.
       */
      host.note("join", who, opened.it.id, map.id);
      return;
    }

    /** "I have stopped." Their link going does the same - see `farewell`. */
    case "unhost": {
      farewell(host, client.id);
      return;
    }

    /** How many are in there. Their count: we cannot see inside their world. */
    case "holding": {
      host.board?.said(
        client.id, String(message.world ?? ""), Number(message.players ?? 0));
      return;
    }

    /** "Put me through to that world." */
    case "offer": {
      const board = host.board;
      if (board === null) {
        send(client, { t: "nocall", why: "not-here" });
        return;
      }
      const to = board.offer(client.id, String(message.world ?? ""),
        String(message.sdp ?? ""), Date.now());
      if (!to.ok) {
        send(client, { t: "nocall", why: to.why });
        return;
      }
      /*
       * The host's link may have gone between the list and this message.
       * Tidy the world away rather than leaving a row nobody answers - a
       * world in the list that nobody is behind is worse than no world.
       */
      const there = host.reach(to.it);
      if (there === null || !there.open()) {
        farewell(host, to.it);
        send(client, { t: "nocall", why: "world" });
        return;
      }
      there.send({ t: "call", from: client.id, sdp: String(message.sdp) });
      return;
    }

    /** The host's half, for one particular person waiting. */
    case "answer": {
      const board = host.board;
      if (board === null) {
        send(client, { t: "nocall", why: "not-here" });
        return;
      }
      const to = board.answer(
        client.id, Number(message.to ?? 0), String(message.sdp ?? ""));
      if (!to.ok) {
        send(client, { t: "nocall", why: to.why });
        return;
      }
      host.reach(to.it)?.send({ t: "answer", sdp: String(message.sdp) });
      return;
    }

    /** One more way of being reached, either way round. */
    case "ice": {
      const board = host.board;
      if (board === null) {
        send(client, { t: "nocall", why: "not-here" });
        return;
      }
      const to = board.candidate(client.id,
        message.to === undefined ? null : Number(message.to),
        String(message.line ?? ""));
      if (!to.ok) {
        send(client, { t: "nocall", why: to.why });
        return;
      }
      host.reach(to.it)?.send(
        { t: "ice", from: client.id, line: String(message.line) });
      return;
    }

    /** A signed-in guest wants a ticket into a world held in a browser. */
    case "ticket": {
      const name = String(message.name ?? "").trim();
      const ok = host.tickets !== undefined && host.names !== null &&
        host.names.hasToken(name, String(message.token ?? ""));
      send(client, ok
        ? { t: "ticket", ticket: host.tickets!.make(name, String(message.world ?? "")) }
        : { t: "ticket" });
      return;
    }

    /** A host asks whether somebody's ticket into its world is real. */
    case "vouch": {
      const name = String(message.name ?? "").trim();
      const world = host.board?.worldOf(client.id) ?? null;
      const ok = world !== null && host.tickets !== undefined &&
        host.tickets.check(String(message.ticket ?? ""), name, world);
      send(client, { t: "vouched", name, ok });
      return;
    }

    /** "We are through." Nothing else would ever tell us. */
    case "through": {
      host.board?.through(client.id);
      return;
    }
  }
}

/**
 * Somebody who was hosting has gone.
 *
 * Their world comes off the list, and everybody who was part-way through
 * being introduced to it is told so rather than left waiting for an answer
 * that is never coming. Called both when they say `unhost` and when their
 * link closes, because from the outside those are the same event and only
 * one of them is polite.
 */
export function farewell(host: Host, who: number): void {
  const stranded = host.board?.gone(who) ?? [];
  for (const guest of stranded) {
    host.reach(guest)?.send({ t: "nocall", why: "world" });
  }
}

export function snapshotOf(client: RealmClient): PlayerState {
  return {
    id: client.id, name: client.name, skinId: client.skinId,
    x: client.x, y: client.y, z: client.z, yaw: client.yaw, pitch: client.pitch,
    // Left out when nobody is flying, which is nearly always: fifteen
    // snapshots a second is not the place for a field that is usually null.
    ...(client.drone !== null ? { drone: client.drone } : {}),
    // Same rule: an empty hand and a man who has never swung are the common
    // case, and the common case should cost nothing.
    ...(client.held > 0 ? { held: client.held } : {}),
    ...(client.blows > 0 ? { blows: client.blows } : {}),
  };
}

/** Is that a place in that world? */
function plausibleDrone(realm: Realm, at: DroneState | undefined): boolean {
  if (at === undefined) return false;
  if (!Number.isFinite(at.x) || !Number.isFinite(at.y) || !Number.isFinite(at.z)) {
    return false;
  }
  return realm.world.withinBounds(
    Math.floor(at.x), Math.floor(at.y), Math.floor(at.z));
}

/**
 * One beat: run the worlds on, and tell everybody where everybody is.
 *
 * Fifteen a second, per world somebody is standing in. A world nobody has
 * opened is a name in a list and costs nothing at all - which is what makes
 * a second map a line of game code rather than a second machine.
 *
 * The clock belongs to whoever is holding these worlds: `setInterval` on our
 * server, the same in a page. What a beat *is* belongs here, so that a world
 * held in a browser runs exactly as one held on our machine.
 */
export function beat(realms: Iterable<Realm>, dt: number): void {
  for (const realm of realms) {
    if (realm.clients.size === 0) continue;
    realm.tick(dt);
    // Each player written once; see core/net/snapshot.
    const list = pieces([...realm.clients.values()]
      .filter((c) => c.ready).map(snapshotOf));
    for (const client of realm.clients.values()) {
      if (!client.ready) continue;
      // Everybody but themselves: their own position is the one thing they
      // know better than the world does, having predicted it.
      const fields: Field[] = [["list", list.filter((p) => p.item.id !== client.id)]];
      client.sendEncoded(snapshotObject("players", fields), snapshotText("players", fields));
    }
  }
}
